import React, { useState, useRef, useEffect } from "react";
import './FallbackPlacesAutocomplete.css';

interface FallbackPlacesAutocompleteProps {
  value: string;
  onChange: (value: string, placeDetails?: any) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  name?: string;
  onPincodeExtracted?: (pincode: string) => void;
  disabled?: boolean;
}

const FallbackPlacesAutocomplete: React.FC<
  FallbackPlacesAutocompleteProps
> = ({
  value,
  onChange,
  placeholder = "Search address",
  className = "",
  id,
  name,
  onPincodeExtracted,
  disabled = false,
}) => {
  const [fallbackPredictions, setFallbackPredictions] = useState<any[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [lastSelectedLocation, setLastSelectedLocation] = useState(value);
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [originalPlaceDetails, setOriginalPlaceDetails] = useState<any>(null);

  useEffect(() => {
    console.log("FPA: Sync useEffect - isFocused:", isFocused, "value:", value, "localValue:", localValue);
    if (!isFocused) {
      setLocalValue(value);
      if (value) {
        setLastSelectedLocation(value);
        fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&countrycodes=in&limit=1`)
          .then(res => res.json())
          .then(data => {
            console.log("FPA: Nominatim geocoding data response count:", data?.length);
            if (data && data[0]) {
              const place = data[0];
              setOriginalPlaceDetails({
                formatted_address: place.display_name,
                geometry: {
                  location: {
                    lat: () => Number(place.lat),
                    lng: () => Number(place.lon),
                  },
                },
              });
            }
          })
          .catch(console.error);
      }
    }
  }, [value, isFocused]);

  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      console.log("FPA: handleDocumentClick triggered - target tag:", target.tagName, "target class:", target.className);
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        inputRef.current &&
        !inputRef.current.contains(target)
      ) {
        console.log("FPA: click outside detected! Closing dropdown and restoring. lastSelectedLocation:", lastSelectedLocation);
        setShowPredictions(false);
        setIsFocused(false);
        if (inputRef.current) {
          const currentVal = inputRef.current.value.trim();
          console.log("FPA: click outside - input currentVal:", currentVal, "lastSelectedLocation:", lastSelectedLocation);
          if (!currentVal || currentVal !== lastSelectedLocation) {
            setLocalValue(lastSelectedLocation);
            onChange(lastSelectedLocation, originalPlaceDetails);
          }
        }
      } else {
        console.log("FPA: click was INSIDE dropdown/input. No restore.");
      }
    };
    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, [lastSelectedLocation, originalPlaceDetails]);

  const searchOpenStreetMap = async (query: string) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}&countrycodes=in&limit=5`
      );

      const data = await response.json();

      setFallbackPredictions(data || []);
      setShowPredictions(true);
    } catch (err) {
      console.error(err);
      setFallbackPredictions([]);
      setShowPredictions(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue, undefined);

    if (newValue.trim().length === 0) {
      setFallbackPredictions([]);
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (newValue.length < 3) {
      setFallbackPredictions([]);
      return;
    }

    debounceRef.current = setTimeout(() => {
      searchOpenStreetMap(newValue);
    }, 400);
  };

  const handleSelect = (place: any) => {
    console.log("FPA: handleSelect called - place display_name:", place.display_name);
    const placeDetails = {
      formatted_address: place.display_name,
      geometry: {
        location: {
          lat: () => Number(place.lat),
          lng: () => Number(place.lon),
        },
      },
    };

    onChange(place.display_name, placeDetails);
    setLocalValue(place.display_name);
    setLastSelectedLocation(place.display_name);
    setOriginalPlaceDetails(placeDetails);
    setIsFocused(false);
    setShowPredictions(false);
    setFallbackPredictions([]);
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
      }}
    >
      <input
        ref={inputRef}
        type="text"
        className={`form-control ${className || ''}`.trim()}
        value={localValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        id={id}
        name={name}
        autoComplete="off"
        disabled={disabled}
        onFocus={() => {
          if (disabled) return;
          setIsFocused(true);
          setShowPredictions(true);
          if (value) {
            setLastSelectedLocation(value);
            if (fallbackPredictions.length === 0) {
              searchOpenStreetMap(value);
            }
          }
          setLocalValue("");
        }}
      />

      {showPredictions && (lastSelectedLocation || fallbackPredictions.length > 0) && (
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: "6px",
            marginTop: "4px",
            maxHeight: "250px",
            overflowY: "auto",
            zIndex: 9999,
            boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
          }}
        >
          {lastSelectedLocation && (
            <div
              onClick={() => setShowPredictions(false)}
              style={{
                padding: "10px",
                cursor: "pointer",
                backgroundColor: "#ebf5ff",
                borderBottom: "1px solid #eee",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 600,
                color: "#0066cc",
              }}
            >
              <span style={{ flex: 1, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                {lastSelectedLocation}
              </span>
            </div>
          )}

          {fallbackPredictions
            .filter((place) => place.display_name !== lastSelectedLocation)
            .map((place) => (
              <div
                key={place.place_id}
                onClick={() => handleSelect(place)}
                style={{
                  padding: "10px",
                  cursor: "pointer",
                  borderBottom: "1px solid #eee",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f5f5f5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#fff";
                }}
              >
                {place.display_name}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default FallbackPlacesAutocomplete;